import { useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Link,
  LinearProgress,
  Pagination,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useQuery } from "@tanstack/react-query";
import axios from "../utils/axios-instance";
import type { AppPagination } from "../types";

type SentMiniAppNotification = {
  id: number;
  title: string;
  message: string;
  link: string | null;
  linkText: string | null;
  createdAt: string;
  recipientCount: number;
  deliveredCount: number;
  readCount: number;
  clickedCount: number;
  attachmentType: "none" | "image" | "video" | "file";
  attachmentUrl: string | null;
  attachments?: Array<{
    attachmentType: "image" | "video" | "file";
    attachmentUrl: string;
  }>;
  targetAllUsers: boolean;
  targetUserIds: number[];
};

type Metrics = {
  broadcasts: number;
  recipients: number;
  seen: number;
  clicked: number;
};

type MiniAppNotificationStats = {
  overall: Metrics & {
    unseen: number;
    uniqueUsers: number;
    uniqueUsersSeen: number;
    uniqueUsersClicked: number;
    avgSecondsToSee: number | null;
    avgSecondsToClick: number | null;
  };
  byLink: { withLink: Metrics; withoutLink: Metrics };
  byMedia: Array<Metrics & { type: string }>;
  byAudience: { allUsers: Metrics; selectedUsers: Metrics };
  daily: Array<Metrics & { day: string }>;
  topLinked: Array<{
    id: number;
    title: string;
    createdAt: string;
    seen: number;
    clicked: number;
  }>;
};

const PAGE_SIZE = 20;

const percent = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;

const fetchStats = async (): Promise<MiniAppNotificationStats> => {
  const response = await axios.get<MiniAppNotificationStats>(
    "/admin/miniapp-notifications/stats",
  );
  return response.data;
};

function StatTile({
  label,
  value,
  caption,
  color,
}: {
  label: string;
  value: string | number;
  caption?: string;
  color?: string;
}) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: "background.paper",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography
        variant="h5"
        fontWeight={700}
        sx={{ mt: 0.25, color: color ?? "text.primary" }}
      >
        {value}
      </Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Box>
  );
}

function StatBar({
  label,
  count,
  total,
  totalLabel,
  color,
}: {
  label: string;
  count: number;
  total: number;
  totalLabel: string;
  color: "primary" | "success" | "warning";
}) {
  const pct = percent(count, total);
  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="baseline">
        <Typography variant="body2" fontWeight={600}>
          {label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <b>{count}</b> / {total} {totalLabel} · {pct}%
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={color}
        sx={{ mt: 0.5, height: 8, borderRadius: 4 }}
      />
    </Box>
  );
}

const fetchSentNotifications = async (
  page: number,
  search: string,
): Promise<AppPagination<SentMiniAppNotification>> => {
  const query = new URLSearchParams({
    page: String(page),
    limit: String(PAGE_SIZE),
  });
  if (search.trim()) {
    query.set("search", search.trim());
  }

  const response = await axios.get<AppPagination<SentMiniAppNotification>>(
    `/admin/miniapp-notifications/sent?${query.toString()}`,
  );
  return response.data;
};

const formatDuration = (seconds: number | null) => {
  if (seconds == null) return "—";
  if (seconds < 90) return `${Math.round(seconds)}s`;
  if (seconds < 5400) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172800) return `${(seconds / 3600).toFixed(1)} h`;
  return `${(seconds / 86400).toFixed(1)} d`;
};

const MEDIA_LABELS: Record<string, string> = {
  none: "No media",
  image: "Image",
  video: "Video",
  file: "File",
};

function SectionTitle({ children }: { children: string }) {
  return (
    <Typography variant="subtitle2" fontWeight={700} color="text.secondary">
      {children}
    </Typography>
  );
}

/** One row of a breakdown table: label + sent/seen/clicked with rates. */
function BreakdownRow({
  label,
  m,
  showClicks,
}: {
  label: string;
  m: Metrics;
  showClicks: boolean;
}) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1.4fr repeat(4, minmax(0, 1fr))",
        gap: 1,
        alignItems: "center",
        py: 0.75,
        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
      <Typography variant="body2">{m.broadcasts}</Typography>
      <Typography variant="body2">{m.recipients}</Typography>
      <Typography variant="body2">
        {m.seen}{" "}
        <Typography component="span" variant="caption" color="text.secondary">
          ({percent(m.seen, m.recipients)}%)
        </Typography>
      </Typography>
      <Typography variant="body2">
        {showClicks ? (
          <>
            {m.clicked}{" "}
            <Typography component="span" variant="caption" color="text.secondary">
              ({percent(m.clicked, m.seen)}%)
            </Typography>
          </>
        ) : (
          "—"
        )}
      </Typography>
    </Box>
  );
}

function BreakdownTable({
  title,
  rows,
}: {
  title: string;
  rows: Array<{ label: string; m: Metrics; showClicks: boolean }>;
}) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: 2,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        minWidth: 0,
      }}
    >
      <SectionTitle>{title}</SectionTitle>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1.4fr repeat(4, minmax(0, 1fr))",
          gap: 1,
          mt: 1,
          pb: 0.5,
          borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {["", "Notifications", "Sent", "Seen", "Clicked"].map((h) => (
          <Typography key={h} variant="caption" color="text.secondary">
            {h}
          </Typography>
        ))}
      </Box>
      {rows.map((r) => (
        <BreakdownRow key={r.label} {...r} />
      ))}
    </Box>
  );
}

function StatsSection({ stats }: { stats: MiniAppNotificationStats }) {
  const { overall, byLink, byMedia, byAudience, daily, topLinked } = stats;
  const linkedSeen = byLink.withLink.seen;
  const maxDaily = Math.max(1, ...daily.map((d) => d.recipients));

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: {
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(4, minmax(0, 1fr))",
          },
        }}
      >
        <StatTile
          label="Notifications sent"
          value={overall.broadcasts}
          caption={`${overall.recipients} sent to ${overall.uniqueUsers} users`}
        />
        <StatTile
          label="Seen"
          value={overall.seen}
          caption={`${percent(overall.seen, overall.recipients)}% of sent · ${overall.unseen} not seen yet`}
          color="#2e7d32"
        />
        <StatTile
          label="Button clicks"
          value={overall.clicked}
          caption={`${overall.uniqueUsersClicked} unique users clicked`}
          color="#1976d2"
        />
        <StatTile
          label="Click rate (links only)"
          value={`${percent(byLink.withLink.clicked, linkedSeen)}%`}
          caption={`${byLink.withLink.clicked} clicks / ${linkedSeen} who saw a link`}
          color="#ed6c02"
        />
        <StatTile
          label="Users reached"
          value={overall.uniqueUsersSeen}
          caption={`${percent(overall.uniqueUsersSeen, overall.uniqueUsers)}% of ${overall.uniqueUsers} users notified`}
        />
        <StatTile
          label="Avg. time to see"
          value={formatDuration(overall.avgSecondsToSee)}
          caption="from send to first view"
        />
        <StatTile
          label="Avg. time to click"
          value={formatDuration(overall.avgSecondsToClick)}
          caption="from send to button click"
        />
        <StatTile
          label="With link / without"
          value={`${byLink.withLink.broadcasts} / ${byLink.withoutLink.broadcasts}`}
          caption="notifications sent"
        />
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" },
        }}
      >
        <BreakdownTable
          title="With link vs without link"
          rows={[
            { label: "With link button", m: byLink.withLink, showClicks: true },
            { label: "Without link", m: byLink.withoutLink, showClicks: false },
          ]}
        />
        <BreakdownTable
          title="By audience"
          rows={[
            {
              label: "All users",
              m: byAudience.allUsers,
              showClicks: byAudience.allUsers.clicked > 0,
            },
            {
              label: "Selected users",
              m: byAudience.selectedUsers,
              showClicks: byAudience.selectedUsers.clicked > 0,
            },
          ]}
        />
        <BreakdownTable
          title="By media type"
          rows={byMedia
            .filter((r) => r.type === "none" || r.broadcasts > 0)
            .map((r) => ({
              label: MEDIA_LABELS[r.type] ?? r.type,
              m: r,
              showClicks: r.clicked > 0,
            }))}
        />
        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            minWidth: 0,
          }}
        >
          <SectionTitle>Top clicked (link notifications)</SectionTitle>
          {topLinked.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No linked notification has been seen yet.
            </Typography>
          ) : (
            <Stack spacing={1} sx={{ mt: 1 }}>
              {topLinked.map((t) => (
                <Box key={t.id}>
                  <Box display="flex" justifyContent="space-between" gap={1}>
                    <Typography variant="body2" fontWeight={600} noWrap>
                      {t.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {t.clicked}/{t.seen} · {percent(t.clicked, t.seen)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={percent(t.clicked, t.seen)}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          p: 1.5,
          borderRadius: 2,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box display="flex" justifyContent="space-between" flexWrap="wrap" gap={1}>
          <SectionTitle>Last 14 days (by send date)</SectionTitle>
          <Box display="flex" gap={1.5}>
            {[
              ["Sent", "action.disabled"],
              ["Seen", "success.main"],
              ["Clicked", "primary.main"],
            ].map(([label, color]) => (
              <Box key={label} display="flex" alignItems="center" gap={0.5}>
                <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: color }} />
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
        {daily.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Nothing sent in the last 14 days.
          </Typography>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 1,
              height: 130,
              mt: 1.5,
              overflowX: "auto",
            }}
          >
            {daily.map((d) => (
              <Box
                key={d.day}
                title={`${d.day}: ${d.recipients} sent, ${d.seen} seen, ${d.clicked} clicked`}
                sx={{
                  flex: "1 0 28px",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "center",
                    gap: "2px",
                    flex: 1,
                  }}
                >
                  {[
                    [d.recipients, "action.disabled"],
                    [d.seen, "success.main"],
                    [d.clicked, "primary.main"],
                  ].map(([value, color], i) => (
                    <Box
                      key={i}
                      sx={{
                        flex: 1,
                        maxWidth: 12,
                        height: `${Math.max(Number(value) > 0 ? 4 : 0, (Number(value) / maxDaily) * 100)}%`,
                        bgcolor: color as string,
                        borderRadius: "3px 3px 0 0",
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                  {d.day.slice(5)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Stack>
  );
}

export function MiniAppNotificationsSection() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");

  const debouncedSearch = useMemo(() => searchInput, [searchInput]);

  const { data, isLoading, isError, error } = useQuery<
    AppPagination<SentMiniAppNotification>,
    Error
  >({
    queryKey: ["miniapp-notifications-sent", page, debouncedSearch],
    queryFn: () => fetchSentNotifications(page, debouncedSearch),
  });

  const { data: stats } = useQuery<MiniAppNotificationStats, Error>({
    queryKey: ["miniapp-notifications-stats"],
    queryFn: fetchStats,
    refetchInterval: 15000,
  });

  const items = data?.items ?? [];
  const totalPages = data?.meta?.totalPages ?? 1;
  const getAttachmentList = (item: SentMiniAppNotification) => {
    if (Array.isArray(item.attachments) && item.attachments.length > 0) {
      return item.attachments;
    }
    if (item.attachmentUrl && item.attachmentType !== "none") {
      return [
        {
          attachmentType: item.attachmentType,
          attachmentUrl: item.attachmentUrl,
        },
      ];
    }
    return [];
  };

  return (
    <Card
      sx={{
        p: { xs: 2, sm: 3 },
      }}
    >
      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="h5" fontWeight="bold" color="text.secondary">
          Sent Mini App Notifications
        </Typography>

        {stats && <StatsSection stats={stats} />}

        <TextField
          size="small"
          fullWidth
          label="Search"
          placeholder="Search by title or message"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            setPage(1);
          }}
        />

        {isError ? (
          <Alert severity="error">
            {error.message || "Failed to load notifications."}
          </Alert>
        ) : isLoading ? (
          <Stack spacing={1.5}>
            {[...Array(4)].map((_, idx) => (
              <Skeleton key={idx} variant="rounded" height={88} />
            ))}
          </Stack>
        ) : items.length === 0 ? (
          <Typography color="text.secondary">
            No sent notifications found.
          </Typography>
        ) : (
          <Stack spacing={1.5}>
            {items.map((item) => (
              <Card key={item.id} variant="outlined">
                <CardContent>
                  <Typography fontWeight={700}>
                    {item.title || "Untitled"}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {item.message || "—"}
                  </Typography>
                  {item.link && (
                    <Link
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{ display: "block", mt: 0.5, wordBreak: "break-all" }}
                    >
                      {item.link}
                    </Link>
                  )}
                  {getAttachmentList(item).map(
                    (attachment, attachmentIndex) => (
                      <Box
                        mt={1.5}
                        key={`${attachment.attachmentUrl}-${attachmentIndex}`}
                      >
                        {attachment.attachmentType === "image" && (
                          <Box
                            component="img"
                            src={attachment.attachmentUrl}
                            alt={item.title || "Notification image"}
                            sx={{
                              width: "100%",
                              maxWidth: 320,
                              borderRadius: 1.5,
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                              objectFit: "cover",
                              maxHeight: 180,
                            }}
                          />
                        )}
                        {attachment.attachmentType === "video" && (
                          <Box
                            component="video"
                            controls
                            src={attachment.attachmentUrl}
                            sx={{
                              width: "100%",
                              maxWidth: 360,
                              borderRadius: 1.5,
                              border: (theme) => `1px solid ${theme.palette.divider}`,
                              maxHeight: 220,
                            }}
                          />
                        )}
                        {attachment.attachmentType === "file" && (
                          <Link
                            href={attachment.attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Open attachment{" "}
                            {getAttachmentList(item).length > 1
                              ? `#${attachmentIndex + 1}`
                              : ""}
                          </Link>
                        )}
                      </Box>
                    ),
                  )}
                  <Box
                    mt={1}
                    display="flex"
                    justifyContent="space-between"
                    flexWrap="wrap"
                    gap={1}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Recipients: {item.recipientCount}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Attachment: {getAttachmentList(item).length || 0}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.createdAt
                        ? new Date(item.createdAt).toLocaleString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </Typography>
                  </Box>
                  <Stack
                    spacing={1.25}
                    sx={{
                      mt: 1.5,
                      p: 1.5,
                      borderRadius: 1.5,
                      backgroundColor: "action.hover",
                    }}
                  >
                    <StatBar
                      label="Seen"
                      count={item.deliveredCount}
                      total={item.recipientCount}
                      totalLabel="recipients"
                      color="success"
                    />
                    {item.link ? (
                      <StatBar
                        label={`Clicked "${item.linkText || "مشاهده"}"`}
                        count={item.clickedCount}
                        total={item.deliveredCount}
                        totalLabel="who saw it"
                        color="primary"
                      />
                    ) : (
                      <Typography variant="caption" color="text.secondary">
                        No link button on this notification, so there are no
                        click stats.
                      </Typography>
                    )}
                  </Stack>
                  <Box mt={1.25}>
                    {item.targetAllUsers ? (
                      <Chip
                        size="small"
                        color="primary"
                        label="Target: All users"
                      />
                    ) : (
                      <Accordion
                        disableGutters
                        elevation={0}
                        sx={{ border: (theme) => `1px solid ${theme.palette.divider}`, borderRadius: 1 }}
                      >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Typography variant="body2" fontWeight={600}>
                            Target users ({item.targetUserIds?.length || 0})
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                          {item.targetUserIds?.length ? (
                            <Box
                              sx={{
                                maxHeight: 120,
                                overflowY: "auto",
                                p: 1,
                                borderRadius: 1,
                                backgroundColor: "background.paper",
                                border: (theme) => `1px dashed ${theme.palette.divider}`,
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{ wordBreak: "break-word" }}
                              >
                                {item.targetUserIds.join(", ")}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              No user IDs stored.
                            </Typography>
                          )}
                        </AccordionDetails>
                      </Accordion>
                    )}
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Stack>
        )}

        <Box display="flex" justifyContent="center" mt={1}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size="large"
          />
        </Box>
      </Box>
    </Card>
  );
}
